
import React from 'react';
import { Button } from '@/components/ui/button';
import { Brain, Download } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { exportToPDF } from '@/utils/pdfExport';
import { useToast } from '@/hooks/use-toast';
import { useIsMobile } from '@/hooks/use-mobile';

interface ResultsFooterProps {
  completedAt: string;
  userData?: {
    fullName: string;
    email: string;
  };
  companyId?: string | null;
}

const ResultsFooter: React.FC<ResultsFooterProps> = ({ completedAt, userData, companyId }) => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const isMobile = useIsMobile();
  
  // Define the Zentrix Insights company ID (general/platform candidates)
  const ZENTRIX_INSIGHTS_COMPANY_ID = '87090009-e2e5-4b96-af27-a8b5691abf85';
  
  // Only show cognitive CTA for real company-linked assessments (not Zentrix Insights default)
  const showCognitiveCTA = !!companyId && companyId !== ZENTRIX_INSIGHTS_COMPANY_ID;
  
  let navigate: ((path: string, options?: any) => void) | null = null;
  try {
    navigate = useNavigate();
  } catch (error) {
    console.log('Navigation not available in this context');
  }

  const handlePDFExport = async () => {
    try {
      await exportToPDF(isMobile);
      toast({
        title: "PDF Downloaded",
        description: "Your behavioral assessment report has been downloaded successfully."
      });
    } catch (error) {
      console.error('PDF export failed:', error);
      toast({
        title: "PDF Export Failed",
        description: "Unable to generate PDF. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleCognitiveAssessment = () => {
    if (navigate) {
      const params = new URLSearchParams();
      if (userData) {
        params.set('name', userData.fullName);
        params.set('email', userData.email);
      }
      // Pass companyId for company-linked Full Assessments
      if (companyId && companyId !== ZENTRIX_INSIGHTS_COMPANY_ID) {
        params.set('companyId', companyId);
      }
      const queryString = params.toString();
      navigate(queryString ? `/cognitive?${queryString}` : '/cognitive');
    }
  };

  return (
    <div className="flex flex-col gap-8 w-full mt-32 pb-12 overflow-x-hidden">
      {/* Cognitive Assessment CTA - Only show for real company-linked assessments */}
      {showCognitiveCTA && (
        <div className="text-center py-12">
          <h3 className="text-xl font-semibold text-foreground mb-2">
            {t('results.cognitiveAssessmentCTA') || 'Complete Your Profile'}
          </h3>
          <p className="text-muted-foreground mb-6">
            {t('results.cognitiveAssessmentDescription') || 'Take the Cognitive Assessment for your full profile'}
          </p>
          <Button onClick={handleCognitiveAssessment}>
            {t('buttons.cognitiveAssessment') || 'Continue to Cognitive Assessment'}
          </Button>
        </div>
      )}

      {/* PDF Download button hidden temporarily
      <Button 
        variant="outline" 
        onClick={handlePDFExport} 
        className="flex items-center gap-3 px-6 py-3 border-2 border-black text-black hover:bg-black hover:text-white transition-colors duration-300 font-semibold tracking-wide"
      >
        <Download className="h-5 w-5" />
        {t('results.downloadPDF') || 'Download PDF'}
      </Button>
      */}
    </div>
  );
};

export default ResultsFooter;
